import React, { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import secondsToMilliseconds from 'date-fns/secondsToMilliseconds';
import { Alert, Button, Checkbox, CircularProgress, LinearProgress, TextField } from '@mui/material';
import Search from '@mui/icons-material/Search';
import Modal from '~/ui/components/modal/index';
import { Text } from '~/ui/index';
import cx from '~/data/utils/helpers/cx';
import { useDebounceValue } from '~/app/utils/debounce';
import { formatDate } from '~/app/utils/format-data';
import {
  clientDataCopyKeys,
  isTerminalCopyStatus,
  useClientDataCopyPreview,
  useGetClientDataCopy,
  useListClientDataCopies,
  useListCopySourceCandidates,
  useStartClientDataCopy,
} from '~/data/client-data-copy/queries';
import {
  COPY_ALREADY_RUNNING_CODE,
  getApiErrorCode,
  getApiErrorMessage,
  getScopeCollisions,
  SCOPE_COLLISION_CODE,
} from '~/data/client-data-copy/errors';
import {
  ClientDataCopyStatus,
  CopySourceMatchReason,
  type ClientDataCopyDto,
  type CopySourceCandidateDto,
  type ScopeCollisionDto,
} from '~/data/openapi-client/index';

export type CopyClientDataPopupProps = {
  /**
   * ALWAYS the target - the client being written to, the one the user is standing on. Nothing in this modal
   * ever passes the source here.
   */
  targetClientId: string;
  targetClientName?: string;
  closeFunction: () => void;
};

type Step = 'pick' | 'confirm' | 'progress';

const MATCH_REASON_LABELS: Record<CopySourceMatchReason, string> = {
  [CopySourceMatchReason.VatNumber]: 'Same VAT number',
  [CopySourceMatchReason.NameSimilarity]: 'Similar name',
  [CopySourceMatchReason.SameCountry]: 'Same country',
  [CopySourceMatchReason.Search]: 'Search result',
};

const STATUS_LABELS: Record<ClientDataCopyStatus, string> = {
  [ClientDataCopyStatus.Planning]: 'Planning the copy',
  [ClientDataCopyStatus.Planned]: 'Planned, waiting to start',
  [ClientDataCopyStatus.CopyingRows]: 'Copying records',
  [ClientDataCopyStatus.CopyingFiles]: 'Copying files',
  [ClientDataCopyStatus.CopyingLogs]: 'Copying report history',
  [ClientDataCopyStatus.Verifying]: 'Verifying the copy',
  [ClientDataCopyStatus.Completed]: 'Completed',
  [ClientDataCopyStatus.Failed]: 'Failed',
};

/** The status arrives as a string; an unknown value must show itself rather than render blank. */
const statusLabel = (status?: ClientDataCopyStatus) => (status && STATUS_LABELS[status]) || status || 'Unknown status';

const isLiveCopy = (copy?: ClientDataCopyDto) => !!copy?.status && !isTerminalCopyStatus(copy.status);

const percentage = (done?: number, total?: number) =>
  total && total > 0 ? Math.min(100, Math.round(((done ?? 0) / total) * 100)) : 0;

/** One block-level line of text. `Text` is a <span>, so stacked lines need a wrapper to break. */
function Line({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <div className={className}>
      <Text>{children}</Text>
    </div>
  );
}

function Field({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div className="flex justify-between items-baseline gap-x-4 py-0.5">
      <Text className="text-sm text-medium shrink-0 whitespace-nowrap">{label}</Text>
      <Text className="text-sm font-semibold text-right break-words min-w-0">{value ?? '-'}</Text>
    </div>
  );
}

function CandidateRow({
  candidate,
  selected,
  onSelect,
}: {
  candidate: CopySourceCandidateDto;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cx(
        'w-full text-left border rounded p-3 mb-2 hover:border-primary-blue',
        selected ? 'border-primary-blue bg-light' : 'border-grey',
      )}
    >
      {/* A single flex child: a <button>'s own box lays its children out, so keep exactly one. */}
      <div className="w-full">
        <div className="flex justify-between items-baseline gap-x-3">
          <Text className="text-sm font-semibold">{candidate.name}</Text>
          <Text className="text-xs text-medium shrink-0 whitespace-nowrap">
            {candidate.matchReason ? MATCH_REASON_LABELS[candidate.matchReason] : ''}
          </Text>
        </div>
        <Line className="text-xs text-medium">
          {candidate.countryCode ?? '-'}
          {' · VAT '}
          {candidate.vatNumber || 'none'}
          {' · Aura '}
          {candidate.auraId || 'none'}
          {' · iPower '}
          {candidate.iPowerId || 'none'}
        </Line>
        <Line className="text-xs text-medium">
          {candidate.reportScopeCount ?? 0} report scope(s)
          {candidate.lastActivity ? ` · last activity ${formatDate(candidate.lastActivity)}` : ' · no activity'}
        </Line>
      </div>
    </button>
  );
}

function CollisionRow({
  collision,
  skipped,
  onToggle,
}: {
  collision: ScopeCollisionDto;
  skipped: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex items-start gap-x-2 border-b border-grey py-1">
      <Checkbox size="small" checked={skipped} onChange={onToggle} />
      <div>
        <Line className="text-sm font-semibold">
          {collision.sourceName} ({collision.scopeType})
        </Line>
        <Line className="text-xs text-medium">
          Period end {collision.periodEnd ? formatDate(collision.periodEnd) : '-'} · the target already holds{' '}
          {collision.targetName}
        </Line>
      </div>
    </div>
  );
}

/**
 * The history disclosure. Its second sentence is the owner's condition on copying `AuditLog.Actor` verbatim:
 * users of the target client will see the email addresses of the source client's staff. Do not soften it, and
 * never claim history is left behind - it is copied.
 */
function HistoryDisclosure({ historyEntryCount }: { historyEntryCount?: number }) {
  return (
    <Alert severity="info" className="mt-3">
      <Line className="text-sm font-semibold">Report history is included.</Line>
      <Line className="text-sm">
        The copied reports keep their original activity feed and processing logs, with the original timestamps and the
        users who performed each action. Those users are members of the source client, and their names and email
        addresses will be visible to everyone with access to this client.
      </Line>
      <Line className="text-sm mt-1">{historyEntryCount ?? 0} history entries will be copied.</Line>
    </Alert>
  );
}

export default function CopyClientDataPopup({
  targetClientId,
  targetClientName,
  closeFunction,
}: CopyClientDataPopupProps) {
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('pick');
  const [search, setSearch] = useState('');
  const [source, setSource] = useState<CopySourceCandidateDto | null>(null);
  const [skippedScopeIds, setSkippedScopeIds] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [copyId, setCopyId] = useState<string | undefined>();
  /** Collisions the server refused on, when they are not already in the preview. */
  const [refusedCollisions, setRefusedCollisions] = useState<ScopeCollisionDto[]>([]);
  const [startError, setStartError] = useState<string | undefined>();

  const debouncedSearch = useDebounceValue(search);

  // A copy already in flight onto this client. Found by listing rather than remembered, so a page reload does
  // not lose track of it - and it is what blocks a second start.
  const { data: existingCopies } = useListClientDataCopies(targetClientId, { limit: 1, offset: 0 });
  const latestCopy = existingCopies?.items?.[0];
  const runningCopy = isLiveCopy(latestCopy) ? latestCopy : undefined;

  const {
    data: candidatePages,
    isLoading: isLoadingCandidates,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useListCopySourceCandidates(targetClientId, debouncedSearch || undefined);

  const candidates = useMemo(() => candidatePages?.pages.flatMap((page) => page.items) ?? [], [candidatePages]);

  const {
    data: preview,
    isLoading: isLoadingPreview,
    isFetching: isFetchingPreview,
    error: previewError,
  } = useClientDataCopyPreview(targetClientId, step === 'pick' ? undefined : source?.id, skippedScopeIds, {
    useErrorBoundary: false,
  });

  const { mutate: startCopy, isLoading: isStarting } = useStartClientDataCopy({
    onSuccess: (copy) => {
      setCopyId(copy.id);
      setRefusedCollisions([]);
      setStartError(undefined);
      setStep('progress');
      queryClient.invalidateQueries({ queryKey: clientDataCopyKeys.base(), exact: false });
    },
    // The 409 carries the clashing scopes; surface them and let the user tick them, never skip silently.
    onError: (error) => {
      const code = getApiErrorCode(error);

      if (code === SCOPE_COLLISION_CODE) {
        setRefusedCollisions(getScopeCollisions(error));
      }

      setStartError(
        getApiErrorMessage(error) ??
          (code === COPY_ALREADY_RUNNING_CODE
            ? 'A copy onto this client is already running.'
            : 'The copy was refused.'),
      );
    },
    useErrorBoundary: false,
  });

  const { data: copy } = useGetClientDataCopy(targetClientId, copyId, {
    // The copy is Completed long before history is. Keep polling while the background log chain is live,
    // otherwise the history line freezes at whatever it happened to read last.
    refetchInterval: (data) =>
      !isTerminalCopyStatus(data?.status) || data?.logCopyStatus === 'Running' ? secondsToMilliseconds(5) : false,
  });

  // Every clash, from the preview and from any refusal, de-duplicated on the source scope id.
  const collisions = useMemo(() => {
    const byId = new Map<string, ScopeCollisionDto>();

    [...(preview?.collisions ?? []), ...refusedCollisions].forEach((collision) => {
      if (collision.sourceScopeId) byId.set(collision.sourceScopeId, collision);
    });

    return [...byId.values()];
  }, [preview?.collisions, refusedCollisions]);

  const unacknowledged = collisions.filter(
    (collision) => !!collision.sourceScopeId && !skippedScopeIds.includes(collision.sourceScopeId),
  );

  const crossCountry = preview?.crossCountry ?? false;
  const copyAlreadyRunning = preview?.copyAlreadyRunning || !!runningCopy;
  const canStart =
    !!source &&
    !!preview &&
    !isFetchingPreview &&
    unacknowledged.length === 0 &&
    !copyAlreadyRunning &&
    (!crossCountry || reason.trim().length > 0);

  const toggleSkipped = (sourceScopeId?: string) => {
    if (!sourceScopeId) return;

    setSkippedScopeIds((current) =>
      current.includes(sourceScopeId) ? current.filter((id) => id !== sourceScopeId) : [...current, sourceScopeId],
    );
  };

  const title =
    step === 'progress' ? 'Copying client data' : `Copy client data onto ${targetClientName ?? 'this client'}`;

  return (
    <Modal
      open
      closeIcon
      onClose={closeFunction}
      maxWidth="md"
      fullWidth
      modalTitle={<Text className="font-semibold">{title}</Text>}
    >
      {step === 'pick' && (
        <div>
          <Line className="text-sm text-medium mb-3">
            Choose the client whose data should be copied <strong>onto</strong> {targetClientName ?? 'this client'}. The
            source client is not modified.
          </Line>

          {copyAlreadyRunning && (
            <Alert severity="warning" className="mb-3">
              A copy onto this client is already running ({statusLabel(runningCopy?.status)}). Wait for it to finish
              before planning another one.
              {!!runningCopy?.id && (
                <Button
                  type="button"
                  variant="text"
                  size="small"
                  onClick={() => {
                    setCopyId(runningCopy.id);
                    setStep('progress');
                  }}
                >
                  View progress
                </Button>
              )}
            </Alert>
          )}

          <TextField
            placeholder="Search clients"
            InputProps={{ startAdornment: <Search /> }}
            onChange={({ target: { value } }) => setSearch(value)}
            value={search}
            size="small"
            fullWidth
            className="mb-3"
          />

          <div className="max-h-80 overflow-y-auto mb-3">
            {isLoadingCandidates ? (
              <CircularProgress size="1.5em" />
            ) : candidates.length === 0 ? (
              <Line className="text-medium">No candidate source clients found.</Line>
            ) : (
              candidates.map((candidate) => (
                <CandidateRow
                  key={candidate.id}
                  candidate={candidate}
                  selected={source?.id === candidate.id}
                  onSelect={() => setSource(candidate)}
                />
              ))
            )}

            {hasNextPage && (
              <Button variant="text" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                {isFetchingNextPage ? 'Loading...' : 'Load more'}
              </Button>
            )}
          </div>

          <div className="flex justify-end gap-x-2">
            <Button type="button" variant="outlined" onClick={closeFunction}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="contained"
              disabled={!source || copyAlreadyRunning}
              onClick={() => setStep('confirm')}
            >
              Continue
            </Button>
          </div>
        </div>
      )}

      {step === 'confirm' && (
        <div>
          {previewError ? (
            <>
              <Alert severity="error">{getApiErrorMessage(previewError) ?? 'The copy could not be previewed.'}</Alert>
              <div className="flex justify-end gap-x-2 mt-4">
                <Button type="button" variant="outlined" onClick={() => setStep('pick')}>
                  Back
                </Button>
              </div>
            </>
          ) : isLoadingPreview || !preview ? (
            <CircularProgress size="1.5em" />
          ) : (
            <>
              <div className="grid grid-cols-2 gap-x-6 mb-4">
                <div>
                  <Line className="text-sm font-semibold mb-2">Source (copied from)</Line>
                  <Field label="Client" value={preview.sourceClientName} />
                  <Field label="Country" value={source?.countryCode} />
                  <Field label="VAT" value={source?.vatNumber || 'none'} />
                  <Field label="Aura id" value={source?.auraId || 'none'} />
                  <Field label="iPower id" value={source?.iPowerId || 'none'} />
                  <Field label="Report scopes to copy" value={preview.reportScopeCount ?? 0} />
                  <Field label="Comparisons to copy" value={preview.comparisonCount ?? 0} />
                </div>
                <div>
                  <Line className="text-sm font-semibold mb-2">Target (copied onto)</Line>
                  <Field label="Client" value={preview.targetClientName} />
                  <Field label="Users gaining access" value={preview.affectedUserCount ?? 0} />
                  <Field label="History entries copied" value={preview.historyEntryCount ?? 0} />
                </div>
              </div>

              <Alert severity="warning" className="mb-3">
                <Line className="text-sm font-semibold">
                  {preview.affectedUserCount ?? 0} users will gain access to this data.
                </Line>
                <Line className="text-sm">
                  Everyone who can reach {preview.targetClientName} will be able to read the copied reports. The data is
                  copied, not shared, so this cannot be undone by unlinking anything.
                </Line>
              </Alert>

              <HistoryDisclosure historyEntryCount={preview.historyEntryCount} />

              {collisions.length > 0 && (
                <div className="mt-3">
                  <Line className="text-sm font-semibold">
                    {collisions.length} report scope(s) already exist on the target
                  </Line>
                  <Line className="text-sm text-medium mb-2">
                    Nothing is merged. Tick a scope to acknowledge the clash and copy everything except that scope and
                    what hangs off it. Unticked clashes block the copy.
                  </Line>
                  {collisions.map((collision) => (
                    <CollisionRow
                      key={collision.sourceScopeId}
                      collision={collision}
                      skipped={!!collision.sourceScopeId && skippedScopeIds.includes(collision.sourceScopeId)}
                      onToggle={() => toggleSkipped(collision.sourceScopeId)}
                    />
                  ))}
                </div>
              )}

              {crossCountry && (
                <TextField
                  label="Reason (required: the two clients are in different countries)"
                  value={reason}
                  onChange={({ target: { value } }) => setReason(value)}
                  required
                  fullWidth
                  multiline
                  minRows={2}
                  inputProps={{ maxLength: 1024 }}
                  className="mt-3"
                />
              )}

              {copyAlreadyRunning && (
                <Alert severity="warning" className="mt-3">
                  A copy onto this client is already running. Wait for it to finish before starting another one.
                </Alert>
              )}

              {startError && (
                <Alert severity="error" className="mt-3">
                  {startError}
                </Alert>
              )}

              <Line className="text-sm text-medium mt-3">
                This runs in the background and may take several hours. The source client is not modified. Once it
                completes, the copied data will not appear in Power BI until PwC refreshes the dataset on their side.
              </Line>

              <div className="flex justify-end gap-x-2 mt-4">
                <Button type="button" variant="outlined" onClick={() => setStep('pick')}>
                  Back
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  disabled={!canStart || isStarting}
                  onClick={() => {
                    if (!source?.id) return;

                    setStartError(undefined);
                    startCopy({
                      // clientId is the TARGET; sourceClientId is what gets read.
                      clientId: targetClientId,
                      sourceClientId: source.id,
                      reason: crossCountry ? reason.trim() : undefined,
                      skippedScopeIds,
                    });
                  }}
                >
                  {isStarting ? 'Starting...' : 'Start copy'}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {step === 'progress' && (
        <div>
          <Line className="text-sm font-semibold mb-1">{statusLabel(copy?.status)}</Line>
          <Line className="text-sm text-medium mb-3">
            {copy?.sourceClientName} to {copy?.targetClientName}. This runs in the background and may take several hours
            - you can close this window and come back to it.
          </Line>

          <Line className="text-sm">
            Records {copy?.copiedEntityCount ?? 0} of {copy?.plannedEntityCount ?? 0}
          </Line>
          <LinearProgress
            variant="determinate"
            value={percentage(copy?.copiedEntityCount, copy?.plannedEntityCount)}
            className="mt-1 mb-3"
          />

          <Line className="text-sm">
            Files {copy?.copiedBlobCount ?? 0} of {copy?.plannedBlobCount ?? 0}
          </Line>
          <LinearProgress
            variant="determinate"
            value={percentage(copy?.copiedBlobCount, copy?.plannedBlobCount)}
            className="mt-1 mb-3"
          />

          {copy?.logCopyStatus && copy.logCopyStatus !== 'Pending' && (
            <>
              <Line className="text-sm text-medium">
                Report history {copy.copiedLogOwnerCount ?? 0} of {copy.plannedLogOwnerCount ?? 0}
              </Line>
              <LinearProgress
                variant="determinate"
                value={percentage(copy.copiedLogOwnerCount, copy.plannedLogOwnerCount)}
                className="mt-1 mb-3"
              />
              {copy.logCopyStatus === 'Running' && (
                <Line className="text-sm text-medium mb-3">
                  History keeps copying in the background after the copy itself is complete.
                </Line>
              )}
            </>
          )}

          <Field label="History entries copied" value={copy?.copiedLogCount ?? 0} />
          <Field label="Users with access to the copied data" value={copy?.affectedUserCount ?? 0} />
          {!!copy?.skippedScopeIds?.length && (
            <Field label="Report scopes skipped as clashing" value={copy.skippedScopeIds.length} />
          )}

          {copy?.warnings && (
            <Alert severity="warning" className="mt-3">
              <Line className="text-sm font-semibold">Some history could not be copied</Line>
              <Line className="text-sm whitespace-pre-line">{copy.warnings}</Line>
            </Alert>
          )}

          {copy?.lastError && copy.status !== ClientDataCopyStatus.Failed && (
            <Alert severity="warning" className="mt-3">
              <Line className="text-sm font-semibold">Last error — the copy is retrying</Line>
              <Line className="text-sm">{copy.lastError}</Line>
            </Alert>
          )}

          {copy?.status === ClientDataCopyStatus.Completed && (
            <Alert severity="success" className="mt-3">
              <Line className="text-sm">The copy is complete.</Line>
              {!!copy.skippedLogCount && (
                <Line className="text-sm">
                  {copy.skippedLogCount} history entries could not be linked and were not copied.
                </Line>
              )}
              {!!copy.skippedFactRowCount && (
                <Line className="text-sm">
                  {copy.skippedFactRowCount} data rows were left behind because a record they depend on was not copied.
                </Line>
              )}
              <Line className="text-sm mt-1">
                The copied data will not appear in Power BI until PwC refreshes the dataset on their side.
              </Line>
            </Alert>
          )}

          {copy?.status === ClientDataCopyStatus.Failed && (
            <Alert severity="error" className="mt-3">
              {copy.failureMessage ?? 'The copy failed.'}
            </Alert>
          )}

          <div className="flex justify-end gap-x-2 mt-4">
            <Button type="button" variant="contained" onClick={closeFunction}>
              Close
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
